import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

const userPool = new CognitoUserPool({
  UserPoolId: userPoolId,
  ClientId: clientId,
});

export interface AuthUser {
  username: string;
  email?: string;
  name?: string;
  groups?: string[];
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export class CognitoAuthService {
  /**
   * Sign in with username and password
   */
  static signIn(username: string, password: string): Promise<AuthUser> {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          const idToken = session.getIdToken().getJwtToken();
          const accessToken = session.getAccessToken().getJwtToken();
          const refreshToken = session.getRefreshToken().getToken();

          const user: AuthUser = {
            username,
            email: session.getIdToken().payload.email,
            name: session.getIdToken().payload.name,
            groups: session.getIdToken().payload['cognito:groups'] || [],
            idToken,
            accessToken,
            refreshToken,
          };

          // Store in localStorage
          localStorage.setItem('cognito_user', JSON.stringify(user));

          resolve(user);
        },
        onFailure: (err) => {
          reject(err);
        },
        newPasswordRequired: (_userAttributes, _requiredAttributes) => {
          reject(new Error('New password required'));
        },
      });
    });
  }

  /**
   * Sign out current user
   */
  static signOut(): void {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
    localStorage.removeItem('cognito_user');
  }

  /**
   * Get current user session
   */
  static getCurrentSession(): Promise<CognitoUserSession | null> {
    return new Promise((resolve) => {
      const currentUser = userPool.getCurrentUser();

      if (!currentUser) {
        resolve(null);
        return;
      }

      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          resolve(null);
          return;
        }

        if (session.isValid()) {
          resolve(session);
        } else {
          resolve(null);
        }
      });
    });
  }

  /**
   * Get current user info from localStorage
   */
  static getCurrentUser(): AuthUser | null {
    const userJson = localStorage.getItem('cognito_user');
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }

  /**
   * Get ID token for API calls
   */
  static async getIdToken(): Promise<string | null> {
    const session = await this.getCurrentSession();
    if (!session) return null;

    return session.getIdToken().getJwtToken();
  }

  /**
   * Check if user is authenticated
   */
  static async isAuthenticated(): Promise<boolean> {
    const session = await this.getCurrentSession();
    return session !== null && session.isValid();
  }

  /**
   * Refresh session tokens
   */
  static refreshSession(): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      const currentUser = userPool.getCurrentUser();

      if (!currentUser) {
        reject(new Error('No current user'));
        return;
      }

      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err || new Error('No session'));
          return;
        }

        const refreshToken = session.getRefreshToken();
        currentUser.refreshSession(refreshToken, (err, newSession) => {
          if (err) {
            reject(err);
            return;
          }

          // Update stored user with new tokens
          const user = this.getCurrentUser();
          if (user && newSession) {
            user.idToken = newSession.getIdToken().getJwtToken();
            user.accessToken = newSession.getAccessToken().getJwtToken();
            localStorage.setItem('cognito_user', JSON.stringify(user));
          }

          resolve(newSession);
        });
      });
    });
  }
}
